import React, { useState, useEffect } from 'react';
import { useAppSelector } from '../../hooks';
import { 
  FitnessGoal, 
  FitnessLevel, 
  InjuryType, 
  EquipmentAccess, 
  TrainingDays, 
  ProgramDuration 
} from '../../types';
import './WorkoutPlanBuilder.css';

interface WorkoutPlanBuilderProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (message: string) => void;
}

const FITNESS_GOALS: { value: FitnessGoal; label: string; icon: string }[] = [
  { value: 'build_muscle', label: 'Build Muscle', icon: '💪' },
  { value: 'weight_loss', label: 'Weight Loss', icon: '🔥' },
  { value: 'weight_gain', label: 'Weight Gain', icon: '📈' },
];

const FITNESS_LEVELS: { value: FitnessLevel; label: string; description: string }[] = [
  { value: 'beginner', label: 'Beginner', description: 'New to exercise or returning after a long break' },
  { value: 'intermediate', label: 'Intermediate', description: '6+ months of consistent training' },
  { value: 'advanced', label: 'Advanced', description: '2+ years of structured training' },
];

const INJURIES: { value: InjuryType; label: string }[] = [
  { value: 'none', label: 'No Injuries' },
  { value: 'neck', label: 'Neck' },
  { value: 'shoulder', label: 'Shoulder' },
  { value: 'chest', label: 'Chest' },
  { value: 'back', label: 'Back' },
  { value: 'waist', label: 'Waist/Lower Back' },
  { value: 'wrist', label: 'Wrist' },
  { value: 'knee', label: 'Knee' },
];

const EQUIPMENT_OPTIONS: { value: EquipmentAccess; label: string; icon: string }[] = [
  { value: 'no_equipment', label: 'No Equipment', icon: '🏠' },
  { value: 'dumbbells', label: 'Dumbbells at Home', icon: '🏋️' },
  { value: 'full_gym', label: 'Access to Gym', icon: '🏢' },
];

const TRAINING_DAYS: { value: TrainingDays; label: string }[] = [
  { value: 3, label: '3 days/week' },
  { value: 4, label: '4 days/week' },
  { value: 5, label: '5 days/week' },
  { value: 6, label: '6 days/week' },
];

const PROGRAM_DURATIONS: { value: ProgramDuration; label: string; description: string }[] = [
  { value: 8, label: '8 Weeks', description: 'Short-term focused program' },
  { value: 12, label: '12 Weeks', description: 'Standard transformation program' },
  { value: 16, label: '16 Weeks', description: 'Extended periodization program' },
];

const ACTIVITY_LEVEL_LABELS: Record<string, string> = {
  sedentary: 'Sedentary',
  light: 'Lightly Active',
  moderate: 'Moderately Active',
  active: 'Active',
  very_active: 'Very Active',
};

export const WorkoutPlanBuilder: React.FC<WorkoutPlanBuilderProps> = ({
  isOpen,
  onClose,
  onGenerate,
}) => {
  const { profile } = useAppSelector((state) => state.profile);
  
  const [fitnessGoal, setFitnessGoal] = useState<FitnessGoal | null>(null);
  const [fitnessLevel, setFitnessLevel] = useState<FitnessLevel | null>(null);
  const [injuries, setInjuries] = useState<InjuryType[]>([]);
  const [equipment, setEquipment] = useState<EquipmentAccess | null>(null);
  const [trainingDays, setTrainingDays] = useState<TrainingDays | null>(null);
  const [programDuration, setProgramDuration] = useState<ProgramDuration | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  
  const totalSteps = 6;

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFitnessGoal(null);
      setFitnessLevel(null);
      setInjuries([]);
      setEquipment(null);
      setTrainingDays(null);
      setProgramDuration(null);
      setError(null);
      setCurrentStep(1);
    }
  }, [isOpen]);

  const toggleInjury = (injury: InjuryType) => {
    if (injury === 'none') {
      // If selecting "none", clear all other injuries
      setInjuries(['none']);
    } else {
      // If selecting an injury, remove "none" and toggle the injury
      setInjuries(prev => {
        const withoutNone = prev.filter(i => i !== 'none');
        if (withoutNone.includes(injury)) {
          return withoutNone.filter(i => i !== injury);
        } else {
          return [...withoutNone, injury];
        }
      });
    }
  };

  const isStepValid = (step: number): boolean => {
    switch (step) {
      case 1: return fitnessGoal !== null;
      case 2: return fitnessLevel !== null;
      case 3: return injuries.length > 0;
      case 4: return equipment !== null;
      case 5: return trainingDays !== null;
      case 6: return programDuration !== null;
      default: return false;
    }
  };

  const canProceed = isStepValid(currentStep);

  const handleNext = () => {
    if (currentStep < totalSteps && canProceed) {
      setCurrentStep(currentStep + 1);
      setError(null);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setError(null);
    }
  };

  const formatPreConditions = (): string => {
    if (!profile?.preConditions || profile.preConditions.length === 0) {
      return 'None reported';
    }
    return profile.preConditions.join(', ');
  };

  const handleGenerate = () => {
    if (!fitnessGoal || !fitnessLevel || injuries.length === 0 || !equipment || !trainingDays || !programDuration) {
      setError('Please complete all fields');
      return;
    }

    // Format the workout preferences into a chat message
    const goalLabel = FITNESS_GOALS.find(g => g.value === fitnessGoal)?.label;
    const levelLabel = FITNESS_LEVELS.find(l => l.value === fitnessLevel)?.label;
    const injuryLabels = injuries.map(i => INJURIES.find(inj => inj.value === i)?.label).join(', ');
    const equipmentLabel = EQUIPMENT_OPTIONS.find(e => e.value === equipment)?.label;

    let profileInfo = '';
    if (profile) {
      const parts: string[] = [];
      if (profile.gender) parts.push(profile.gender.charAt(0).toUpperCase() + profile.gender.slice(1));
      if (profile.age) parts.push(`${profile.age} years old`);
      if (profile.height) parts.push(`${profile.height} cm`);
      if (profile.weight) parts.push(`${profile.weight} kg`);
      if (profile.activityLevel) parts.push(`${ACTIVITY_LEVEL_LABELS[profile.activityLevel] || profile.activityLevel} lifestyle`);
      if (parts.length > 0) {
        profileInfo = `\n\nMy Profile: ${parts.join(', ')}`;
      }
    }

    const preConditions = formatPreConditions();
    const preConditionsInfo = preConditions !== 'None reported' 
      ? `\nPre-existing Conditions: ${preConditions}` 
      : '';

    const message = `I want to create a personalized workout plan with the following preferences:

🎯 Goal: ${goalLabel}
📊 Fitness Level: ${levelLabel}
⚠️ Injuries/Limitations: ${injuryLabels}
🏋️ Equipment: ${equipmentLabel}
📅 Training Days: ${trainingDays} days per week
⏱️ Program Duration: ${programDuration} weeks${profileInfo}${preConditionsInfo}

Please create a detailed ${programDuration}-week workout plan that:
1. Is safe for my injuries and health conditions
2. Matches my fitness level with appropriate progression
3. Uses only the equipment I have access to
4. Distributes ${trainingDays} training days evenly with proper rest days
5. Includes specific exercises, sets, reps, and rest periods
6. Has clear weekly progression guidelines

Please ensure all exercises are safe given my injuries and pre-existing conditions.`;

    onGenerate(message);
    onClose();
  };

  if (!isOpen) return null;

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="workout-step">
            <h3>What's your fitness goal?</h3>
            <div className="goal-options">
              {FITNESS_GOALS.map(goal => (
                <button
                  key={goal.value}
                  className={`goal-card ${fitnessGoal === goal.value ? 'selected' : ''}`}
                  onClick={() => setFitnessGoal(goal.value)}
                >
                  <span className="goal-icon">{goal.icon}</span>
                  <span className="goal-label">{goal.label}</span>
                </button>
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="workout-step">
            <h3>What's your fitness level?</h3>
            <div className="level-options">
              {FITNESS_LEVELS.map(level => (
                <button
                  key={level.value}
                  className={`level-card ${fitnessLevel === level.value ? 'selected' : ''}`}
                  onClick={() => setFitnessLevel(level.value)}
                >
                  <span className="level-label">{level.label}</span>
                  <span className="level-desc">{level.description}</span>
                </button>
              ))}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="workout-step">
            <h3>Do you have any injuries?</h3>
            <p className="step-hint">Select all that apply</p>
            <div className="injury-options">
              {INJURIES.map(injury => (
                <button
                  key={injury.value}
                  className={`injury-chip ${injuries.includes(injury.value) ? 'selected' : ''}`}
                  onClick={() => toggleInjury(injury.value)}
                >
                  {injury.label}
                </button>
              ))}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="workout-step">
            <h3>What equipment do you have?</h3>
            <div className="equipment-options">
              {EQUIPMENT_OPTIONS.map(eq => (
                <button
                  key={eq.value}
                  className={`equipment-card ${equipment === eq.value ? 'selected' : ''}`}
                  onClick={() => setEquipment(eq.value)}
                >
                  <span className="equipment-icon">{eq.icon}</span>
                  <span className="equipment-label">{eq.label}</span>
                </button>
              ))}
            </div>
          </div>
        );

      case 5:
        return (
          <div className="workout-step">
            <h3>How many days can you train?</h3>
            <div className="days-options">
              {TRAINING_DAYS.map(day => (
                <button
                  key={day.value}
                  className={`days-card ${trainingDays === day.value ? 'selected' : ''}`}
                  onClick={() => setTrainingDays(day.value)}
                >
                  <span className="days-number">{day.value}</span>
                  <span className="days-label">days/week</span>
                </button>
              ))}
            </div>
          </div>
        );

      case 6:
        return (
          <div className="workout-step">
            <h3>Choose your program duration</h3>
            <div className="duration-options">
              {PROGRAM_DURATIONS.map(dur => (
                <button
                  key={dur.value}
                  className={`duration-card ${programDuration === dur.value ? 'selected' : ''}`}
                  onClick={() => setProgramDuration(dur.value)}
                >
                  <span className="duration-weeks">{dur.label}</span>
                  <span className="duration-desc">{dur.description}</span>
                </button>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="workout-builder-overlay" onClick={onClose}>
      <div className="workout-builder-modal" onClick={e => e.stopPropagation()}>
        <div className="workout-builder-header">
          <h2>🏋️ Workout Plan Builder</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        {/* Progress indicator */}
        <div className="workout-progress">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            />
          </div>
          <span className="progress-text">Step {currentStep} of {totalSteps}</span>
        </div>

        <div className="workout-builder-body">
          <p className="workout-disclaimer"><strong>Currently this is an applicaiton under development, the information provided may not match accuray</strong></p>
          {error && <div className="workout-error">{error}</div>}
          
          {/* Profile summary card */}
          {profile && (currentStep === 1) && (
            <div className="profile-summary-card">
              <div className="profile-summary-title">Your Profile</div>
              <div className="profile-summary-content">
                {profile.gender && <span>{profile.gender.charAt(0).toUpperCase() + profile.gender.slice(1)}</span>}
                {profile.age && <span>{profile.age} years</span>}
                {profile.height && <span>{profile.height} cm</span>}
                {profile.weight && <span>{profile.weight} kg</span>}
                {profile.activityLevel && <span>{ACTIVITY_LEVEL_LABELS[profile.activityLevel]}</span>}
              </div>
              {profile.preConditions && profile.preConditions.length > 0 && (
                <div className="profile-conditions">
                  <strong>Pre-conditions:</strong> {profile.preConditions.slice(0, 3).join(', ')}
                  {profile.preConditions.length > 3 && ` +${profile.preConditions.length - 3} more`}
                </div>
              )}
            </div>
          )}

          {renderStep()}
        </div>

        <div className="workout-builder-footer">
          <button 
            className="btn-secondary" 
            onClick={currentStep === 1 ? onClose : handleBack}
          >
            {currentStep === 1 ? 'Cancel' : 'Back'}
          </button>
          
          {currentStep < totalSteps ? (
            <button
              className="btn-primary"
              onClick={handleNext}
              disabled={!canProceed}
            >
              Next
            </button>
          ) : (
            <button
              className="btn-primary generate-btn"
              onClick={handleGenerate}
              disabled={!canProceed}
            >
              🚀 Generate Workout Plan
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
